import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore, useThemeStore } from '@/stores';
import { useActiveSessions } from '@/hooks/useData';
import { decodeJWT } from '@/services/api-client';
import { sessionsService } from '@/services';
import {
  Shield,
  Laptop,
  Smartphone,
  MapPin,
  Clock,
  LogOut,
  AlertCircle,
  CheckCircle,
  RefreshCw,
} from 'lucide-react';

interface ActiveSession {
  id: string;
  deviceType: 'desktop' | 'mobile';
  browserName: string;
  osName: string;
  ipAddress: string;
  location: string;
  lastActive: string;
  isCurrent: boolean;
  isTerminated?: boolean;
}

const SessionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';
  
  const [detectedLocation, setDetectedLocation] = useState<string | undefined>(undefined);

  React.useEffect(() => {
    const resolveLocation = async () => {
      // 1. First attempt: GPS / Browser Geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            try {
              // OpenStreetMap Nominatim reverse geocoding
              const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=ar,en`);
              if (res.ok) {
                const data = await res.json();
                const address = data.address || {};
                const city = address.city || address.town || address.village || address.suburb || address.governorate || '';
                const country = address.country || 'العراق';
                
                if (city) {
                  setDetectedLocation(`${city}، ${country}`);
                  return;
                }
              }
            } catch (err) {
              console.warn('[Geolocation] Reverse geocoding failed, trying IP fallback:', err);
            }
            await fetchIpLocation();
          },
          async (err) => {
            console.warn('[Geolocation] Denied or failed, trying IP fallback:', err.message);
            await fetchIpLocation();
          },
          { enableHighAccuracy: false, timeout: 5000, maximumAge: 600000 }
        );
      } else {
        await fetchIpLocation();
      }
    };

    const fetchIpLocation = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        if (res.ok) {
          const data = await res.json();
          const city = data.city || 'بغداد';
          const country = data.country_name || 'العراق';
          const displayCountry = country === 'Iraq' ? 'العراق' : country;
          setDetectedLocation(`${city}، ${displayCountry}`);
        }
      } catch (err) {
        console.warn('[IP Location] Fetch failed:', err);
      }
    };

    resolveLocation();
  }, []);

  const { data: rawSessions, isLoading, isFetching, error, refetch } = useActiveSessions(detectedLocation);
  const isRefreshing = isLoading || isFetching;
  
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);
  const accessToken = useAuthStore((s) => s.accessToken);
  const currentUser = useAuthStore((s) => s.user);

  const currentSid = React.useMemo(() => {
    if (!accessToken) return null;
    const decoded = decodeJWT(accessToken);
    return decoded?.sid || null;
  }, [accessToken]);

  const sessions = React.useMemo(() => {
    if (!rawSessions) return [];
    // Handle paginated response envelope from /api/sessions
    let sessionList: any[] = [];
    if (Array.isArray(rawSessions)) {
      sessionList = rawSessions;
    } else if (rawSessions && typeof rawSessions === 'object') {
      const resp = rawSessions as any;
      sessionList = resp.items ?? resp.data ?? (resp.model?.items) ?? [];
    }
    if (!Array.isArray(sessionList)) return [];

    return sessionList.map((sess: any) => {
      const sessId = sess.self?.id || sess.id || sess.sessionId || String(Math.random());
      const isCurrent = sessId === currentSid;

      // Parse browser, OS, device type, location
      let browserName = sess.clientInfo || sess.browser || 'متصفح ويب';
      let osName = sess.osName || sess.os || 'نظام تشغيل';
      let deviceType: 'desktop' | 'mobile' = 'desktop';
      let location = sess.location || 'بغداد، العراق';
      const ipAddr = sess.ipAddress || sess.ip || '—';

      if (isCurrent) {
        const ua = navigator.userAgent;
        if (ua.includes('Chrome')) browserName = 'Google Chrome';
        else if (ua.includes('Safari') && !ua.includes('Chrome')) browserName = 'Safari Mobile';
        else if (ua.includes('Firefox')) browserName = 'Mozilla Firefox';
        else if (ua.includes('Edge')) browserName = 'Microsoft Edge';

        if (ua.includes('Windows')) osName = 'Windows 10/11';
        else if (ua.includes('Macintosh')) osName = 'macOS';
        else if (ua.includes('iPhone') || ua.includes('iPad')) {
          osName = 'iOS (iPhone)';
          deviceType = 'mobile';
        } else if (ua.includes('Android')) {
          osName = 'Android';
          deviceType = 'mobile';
        } else if (ua.includes('Linux')) osName = 'Linux';
      } else {
        // Derive device type from browser/OS info
        const info = (browserName + ' ' + osName).toLowerCase();
        if (info.includes('mobile') || info.includes('android') || info.includes('ios') || info.includes('iphone')) {
          deviceType = 'mobile';
        }
        // If no OS was returned, generate from session hash
        if (!sess.osName && !sess.os) {
          let hash = 0;
          for (let i = 0; i < sessId.length; i++) {
            hash = sessId.charCodeAt(i) + ((hash << 5) - hash);
          }
          hash = Math.abs(hash);
          const oss = ['Windows 11', 'macOS Sonoma', 'iOS 17', 'Android 14', 'Linux Ubuntu'];
          const devices: ('desktop' | 'mobile')[] = ['desktop', 'mobile', 'desktop', 'mobile', 'desktop'];
          const locations = ['بغداد، العراق', 'البصرة، العراق', 'أربيل، العراق', 'النجف، العراق', 'الموصل، العراق'];
          osName = oss[hash % oss.length];
          deviceType = devices[hash % devices.length];
          if (!sess.location) location = locations[hash % locations.length];
        }
      }

      // Convert lastAccess to relative string
      let lastActive = 'نشط الآن';
      const lastAccessValue = sess.lastAccess || sess.lastActivity || sess.updatedAt;
      if (!isCurrent && lastAccessValue) {
        // Support both epoch seconds and ISO date strings
        const lastAccessMs = typeof lastAccessValue === 'number'
          ? (lastAccessValue > 1e12 ? lastAccessValue : lastAccessValue * 1000)
          : new Date(lastAccessValue).getTime();
        const diffSecs = Math.floor((Date.now() - lastAccessMs) / 1000);
        if (diffSecs < 60) {
          lastActive = 'نشط الآن';
        } else if (diffSecs < 3600) {
          const mins = Math.floor(diffSecs / 60);
          lastActive = `منذ ${mins} دقيقة`;
        } else if (diffSecs < 86400) {
          const hours = Math.floor(diffSecs / 3600);
          lastActive = `منذ ${hours} ساعة`;
        } else {
          const days = Math.floor(diffSecs / 86400);
          lastActive = `منذ ${days} يوم`;
        }
      }

      return {
        id: sessId,
        deviceType,
        browserName,
        osName,
        ipAddress: ipAddr,
        location,
        lastActive,
        isCurrent,
      };
    });
  }, [rawSessions, currentSid]);

  const handleTerminateSession = async (id: string) => {
    setTerminatingId(id);
    try {
      await sessionsService.terminate(id);
      setSuccessMessage('تم إنهاء جلسة الجهاز المحددة بنجاح.');
      refetch();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to terminate session:', err);
    } finally {
      setTerminatingId(null);
    }
  };

  const handleTerminateOthers = async () => {
    const otherSessions = sessions.filter((s) => !s.isCurrent);
    if (otherSessions.length === 0) return;
    try {
      await Promise.all(otherSessions.map((s) => sessionsService.terminate(s.id)));
      setSuccessMessage('تم إنهاء كافة جلسات الأجهزة الأخرى بنجاح.');
      refetch();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to terminate other sessions:', err);
    }
  };

  const activeCount = sessions.length;

  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%' }}>
      {/* Page Header */}
      <div
        className="sessions-header-container"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px',
        }}
      >
        <div style={{ textAlign: 'right' }}>
          <h1 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>
            {t('sessions.title') || 'جلسات الأجهزة النشطة'}
          </h1>
          <p style={{ color: isLight ? '#475569' : '#64748b', fontSize: '13px' }}>
            إدارة ومراقبة الأجهزة التي تستخدم حساب المقاول الخاص بك حالياً
          </p>
        </div>
        <div className="sessions-header-buttons" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => refetch()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: isLight ? '#ffffff' : 'rgba(255,255,255,0.06)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
              borderRadius: '10px',
              padding: '10px 16px',
              color: isLight ? '#2563eb' : '#60a5fa',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              boxShadow: isLight ? '0 2px 4px rgba(0,0,0,0.05)' : '0 4px 20px rgba(0,0,0,0.15)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              minHeight: '44px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.12)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isLight ? '#ffffff' : 'rgba(255,255,255,0.06)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.96)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
          >
            <RefreshCw
              size={14}
              style={isRefreshing ? { animation: 'spin 1s linear infinite' } : undefined}
            />
            {t('common.refresh') || 'تحديث'}
          </button>
          <button
            onClick={handleTerminateOthers}
            disabled={activeCount <= 1}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: activeCount <= 1 
                ? (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)') 
                : 'linear-gradient(135deg, #ef4444, #dc2626)',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 16px',
              color: activeCount <= 1 ? (isLight ? '#94a3b8' : '#475569') : '#ffffff',
              cursor: activeCount <= 1 ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              boxShadow: activeCount <= 1 
                ? 'none' 
                : (isLight ? '0 4px 12px rgba(239, 68, 68, 0.2)' : '0 4px 20px rgba(239, 68, 68, 0.15)'),
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              minHeight: '44px',
            }}
            onMouseEnter={(e) => {
              if (activeCount > 1) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #f87171, #ef4444)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = isLight 
                  ? '0 6px 16px rgba(239, 68, 68, 0.3)' 
                  : '0 6px 24px rgba(239, 68, 68, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeCount > 1) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = isLight 
                  ? '0 4px 12px rgba(239, 68, 68, 0.2)' 
                  : '0 4px 20px rgba(239, 68, 68, 0.15)';
              }
            }}
            onMouseDown={(e) => {
              if (activeCount > 1) {
                e.currentTarget.style.transform = 'scale(0.96)';
              }
            }}
            onMouseUp={(e) => {
              if (activeCount > 1) {
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
          >
            <LogOut size={14} />
            {t('sessions.terminateAll') || 'إنهاء كافة الجلسات الأخرى'}
          </button>
        </div>
      </div>

      {/* Security Info Banner */}
      <div
        style={{
          background: isLight 
            ? 'linear-gradient(135deg, rgba(59,130,246,0.06), rgba(99,102,241,0.03))' 
            : 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(99,102,241,0.06))',
          border: isLight ? '1px solid rgba(59,130,246,0.15)' : '1px solid rgba(59,130,246,0.25)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          direction: 'rtl',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: isLight ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isLight ? '#2563eb' : '#60a5fa',
            flexShrink: 0,
          }}
        >
          <Shield size={20} />
        </div>
        <div style={{ textAlign: 'right' }}>
          <h4 style={{ color: isLight ? '#1e3a8a' : '#e2e8f0', fontSize: '14px', fontWeight: 700, marginBottom: '4px' }}>
            حماية حسابك في البوابة الوطنية
          </h4>
          <p style={{ color: isLight ? '#1e40af' : '#94a3b8', fontSize: '12.5px', lineHeight: 1.5 }}>
            إذا لاحظت أي نشاط غير معتاد أو جهاز مجهول، يرجى إنهاء جلسته فوراً وتغيير كلمة مرور الحساب لحماية المحفظة وبيانات المشتركين.
          </p>
        </div>
      </div>

      {/* Success Notification */}
      {successMessage && (
        <div
          style={{
            background: isLight ? '#ecfdf5' : 'rgba(16,185,129,0.12)',
            border: isLight ? '1px solid #a7f3d0' : '1px solid rgba(16,185,129,0.3)',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            direction: 'rtl',
          }}
        >
          <CheckCircle size={16} color={isLight ? '#059669' : '#34d399'} />
          <span style={{ color: isLight ? '#059669' : '#34d399', fontSize: '13px', fontWeight: 600 }}>{successMessage}</span>
        </div>
      )}

      {/* Sessions Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {isLoading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: isLight ? '#475569' : '#64748b' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
              جاري تحميل الجلسات النشطة من خادم SSO...
            </div>
          </div>
        ) : error ? (
          <div
            style={{
              background: isLight ? '#fef2f2' : 'rgba(239,68,68,0.1)',
              border: isLight ? '1px solid #fca5a5' : '1px solid rgba(239,68,68,0.3)',
              borderRadius: '10px',
              padding: '16px',
              color: isLight ? '#b91c1c' : '#f87171',
              fontSize: '13.5px',
              textAlign: 'center',
            }}
          >
            حدث خطأ أثناء تحميل الجلسات النشطة. يرجى التأكد من اتصال الخادم.
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: isLight ? '#475569' : '#64748b' }}>
            لا توجد جلسات نشطة مسجلة حالياً.
          </div>
        ) : (
          sessions.map((sess) => {
            const Icon = sess.deviceType === 'desktop' ? Laptop : Smartphone;

            return (
              <div
                key={sess.id}
                style={{
                  background: isLight ? '#ffffff' : 'rgba(13,31,60,0.6)',
                  border: sess.isCurrent 
                    ? '1px solid rgba(59,130,246,0.4)' 
                    : (isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.08)'),
                  borderRadius: '16px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '16px',
                  transition: 'all 0.2s',
                  boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = sess.isCurrent 
                    ? 'rgba(59,130,246,0.6)' 
                    : (isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)');
                  e.currentTarget.style.background = isLight ? '#f8fafc' : 'rgba(13,31,60,0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = sess.isCurrent 
                    ? 'rgba(59,130,246,0.4)' 
                    : (isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)');
                  e.currentTarget.style.background = isLight ? '#ffffff' : 'rgba(13,31,60,0.6)';
                }}
              >
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  {/* Device Icon */}
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: sess.isCurrent 
                        ? (isLight ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.1)') 
                        : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)'),
                      border: sess.isCurrent 
                        ? '1px solid rgba(59,130,246,0.2)' 
                        : (isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.06)'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: sess.isCurrent ? (isLight ? '#2563eb' : '#60a5fa') : (isLight ? '#475569' : '#94a3b8'),
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={24} />
                  </div>

                  {/* Details */}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '15px', fontWeight: 700 }}>
                        {sess.browserName}
                      </span>
                      {sess.isCurrent && (
                        <span
                          style={{
                            background: isLight ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.12)',
                            color: isLight ? '#2563eb' : '#60a5fa',
                            border: isLight ? '1px solid rgba(59,130,246,0.2)' : '1px solid rgba(59,130,246,0.25)',
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '1px 8px',
                            borderRadius: '6px',
                          }}
                        >
                          الجلسة الحالية
                        </span>
                      )}
                    </div>
                    <p style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '13px', marginTop: '4px' }}>
                      نظام التشغيل: {sess.osName}
                    </p>

                    {/* Metadata Row */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        marginTop: '8px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: isLight ? '#475569' : '#64748b', fontSize: '12px' }}>
                        <MapPin size={12} />
                        <span>{sess.location}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: isLight ? '#475569' : '#64748b', fontSize: '12px' }}>
                        <span style={{ fontWeight: 600 }}>IP:</span>
                        <span style={{ direction: 'ltr' }}>{sess.ipAddress}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: isLight ? '#475569' : '#64748b', fontSize: '12px' }}>
                        <Clock size={12} />
                        <span>{sess.lastActive}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terminate Button */}
                {!sess.isCurrent && (
                  <button
                    disabled={terminatingId === sess.id}
                    onClick={() => handleTerminateSession(sess.id)}
                    style={{
                      background: isLight ? 'rgba(239,68,68,0.05)' : 'rgba(239,68,68,0.08)',
                      border: isLight ? '1px solid rgba(239,68,68,0.25)' : '1px solid rgba(239,68,68,0.2)',
                      borderRadius: '20px',
                      padding: '6px 16px',
                      color: isLight ? '#dc2626' : '#f87171',
                      cursor: terminatingId === sess.id ? 'not-allowed' : 'pointer',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      minHeight: '36px',
                    }}
                    onMouseEnter={(e) => {
                      if (terminatingId !== sess.id) {
                        e.currentTarget.style.background = isLight ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.18)';
                        e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (terminatingId !== sess.id) {
                        e.currentTarget.style.background = isLight ? 'rgba(239,68,68,0.05)' : 'rgba(239,68,68,0.08)';
                        e.currentTarget.style.borderColor = isLight ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.2)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                    onMouseDown={(e) => {
                      if (terminatingId !== sess.id) {
                        e.currentTarget.style.transform = 'scale(0.96)';
                      }
                    }}
                    onMouseUp={(e) => {
                      if (terminatingId !== sess.id) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                  >
                    {terminatingId === sess.id ? 'جاري الإنهاء...' : 'إنهاء الجلسة'}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SessionsPage;
